<template>
  <div>
    <div
      v-if="readyToProgress && !tiny"
      class="col-12"
    >
      <banner
        type="success"
        show-icon
        :message="$t('run.collectData.meetingCriteria')"
        class="q-mb-md"
      >
        <template #action>
          <div class="row items-center q-col-gutter-md">
            <div>
              <q-btn
                :label="$t('run.collectData.proceedWithCriteria')"
                flat
                color="dark"
                @click="onProceed"
              />
            </div>
            <div>
              <q-btn
                icon="close"
                flat
                :label="$q.platform.is.mobile ? $t('common.buttonDismiss') : null"
                :round="$q.platform.is.mobile"
                color="dark"
                @click="onDismissMastering"
              >
                <q-tooltip
                  v-if="!$q.platform.is.mobile"
                >
                  {{ $t('common.buttonDismiss') }}
                </q-tooltip>
              </q-btn>
            </div>
          </div>
        </template>
      </banner>
    </div>
    <div
      :class="{
        'no-wrap': dense && tiny,
        'q-col-gutter-sm': dense && tiny,
        'q-col-gutter-md': !dense || !tiny
      }"
      class="row"
    >
      <span
        v-if="readyToProgress && tiny"
      >
        <q-card
          class="col-auto column full-height noselect cursor-pointer"
          @click="onProceedDialog"
        >
          <q-card-section
            class="col row items-center text-center"
          >
            <q-btn
              icon="emoji_events"
              color="positive"
              flat
            />
          </q-card-section>
        </q-card>
      </span>
      <span
        class="items-end row"
      >
        <q-card
          class="col-auto column full-height noselect"
          :class="{
            'sbt-current-card': currentMode === 'SR',
            'sbt-current-card--eo': currentMode === 'EO',
            'sbt-current-card--wide': !tiny,
          }"
        >
          <q-card-section
            class="col column q-px-md text-start"
          >
            <div
              class="full-width row items-center no-wrap"
              :class="{ 'justify-end': tiny }"
            >
              <hr-p
                v-if="!tiny"
                class="col q-mb-none hr-body-small text-uppercase sbt-current-label"
                :class="{ 'sbt-current-label--eo': currentMode === 'EO' }"
                :text="$t('run.collectData.textCurrentMode')"
              />
              <div class="col-auto q-ml-sm hr-body-small no-wrap q-px-sm q-py-xs sbt-current-trial">
                {{ $t('run.collectData.textCurrentTrial', { currentTrial }) }}
              </div>
            </div>
            <hr-p
              class="full-width q-mb-none q-mt-sm hr-body-large sbt-current-mode"
              :class="{ 'sbt-current-mode--eo': currentMode === 'EO' }"
              :text="currentMode"
            />
            <hr-segmented-btn
              v-model="challengeMode"
              class="full-width q-mt-md sbt-current-modes"
              :options="challengeModes"
              :disable="disable"
              size="sm"
              spread
              hide-selected-check
              :legacy-props="{
                size: 'sm',
                dense: !isPinned,
                textColor: 'dark',
                toggleColor: 'dark',
                toggleTextColor: 'white',
              }"
            />
          </q-card-section>
        </q-card>
      </span>
      <span
        v-for="target in currentTasks"
        :key="target.id"
      >
        <target-data
          ref="targets"
          :session-program="sessionProgram"
          :target="target"
          :data="data"
          :active-prompt-levels="activePromptLevels"
          :dense="dense"
          :tiny="tiny"
          :disable="disable"
          :workflow-statuses="workflowStatuses"
          :prevent-duplicates-on-trial="currentTrial"
          :session-paused="sessionPaused"
          :session-duration-offset="sessionDurationOffset"
          @record="onRecordData"
          @record-program-data="onRecordProgramData"
        />
      </span>
      <span
        class=" items-end row"
      >
        <q-card
          class="col-auto column full-height noselect"
          :class="{
            'cursor-pointer': (nextMode === 'SR') && !disable,
            'text-grey-7 cursor-not-allowed': disable,
            'sbt-next-card--eo': nextMode === 'EO',
            'sbt-next-card--wide': (nextMode === 'EO') && !tiny,
            'bg-green-2': nextMode === 'SR'}"
          @click="onSwitchMode"
        >
          <q-card-section
            class="col column"
            :class="nextMode === 'EO'
              ? 'text-start q-px-md'
              : 'flex-center text-center q-px-lg'"
          >
            <template v-if="nextMode === 'EO'">
              <div class="full-width row items-center no-wrap">
                <hr-p
                  class="col q-mb-none hr-body-small sbt-next-eo-label"
                  :text="nextLabel"
                />
                <div
                  v-if="!tiny"
                  class="col-auto q-ml-sm hr-body-small q-py-xs q-px-sm sbt-next-eo-chip"
                  :class="eoStepManuallySelected ? 'sbt-next-eo-chip--manual' : 'sbt-next-eo-chip--randomized'"
                >
                  {{ eoChipLabel }}
                </div>
              </div>
              <div class="full-width q-mt-xs sbt-next-eo-step">
                <hr-p
                  class="q-mb-none hr-body-small sbt-next-eo-branch"
                  :text="eoBranchLabel"
                />
                <hr-p
                  class="q-mb-none hr-body-large sbt-next-eo-name"
                  :text="eoStepLabel"
                />
              </div>
              <div
                v-if="eoStepGroups.length"
                class="full-width row"
                :class="{ 'justify-center': tiny }"
              >
                <hr-supporting-btn
                  :label="tiny ? null : $t('run.collectData.buttonSelectStep')"
                  :icon="tiny ? 'list' : null"
                  size="sm"
                  :disable="disable"
                  class="sbt-next-eo-action q-mt-md"
                  :class="{ 'full-width': !tiny }"
                  :legacy-props="{ noCaps: true }"
                  @click.stop
                >
                  <hr-tooltip v-if="tiny">
                    {{ $t('run.collectData.buttonSelectStep') }}
                  </hr-tooltip>
                  <hr-menu
                    class="sbt-select-step-menu"
                    fit
                    :offset="[0, 6]"
                    :max-height="$q.screen.lt.sm ? '80vh' : '338px'"
                  >
                    <hr-list>
                      <template
                        v-for="(group, groupIndex) in eoStepGroups"
                        :key="groupIndex"
                      >
                        <hr-list-section-header v-if="group.subHeader">
                          {{ group.subHeader }}
                        </hr-list-section-header>
                        <hr-list-item
                          v-for="option in group.options"
                          :key="option.subTargetId || option.targetId"
                          v-close-popup
                          clickable
                          :label="option.label"
                          :caption="option.caption"
                          :active="isSelectedStep(option)"
                          @click="onSelectEOStep(option)"
                        />
                      </template>
                    </hr-list>
                  </hr-menu>
                </hr-supporting-btn>
              </div>
            </template>
            <hr-p
              v-else
              class="full-width hr-body-large q-mb-none"
              :text="nextLabel"
            />
          </q-card-section>
        </q-card>
      </span>
    </div>
  </div>
</template>
<script>
import TargetData from 'components/Programs/DataCollection/TargetData';
import Banner from 'components/UI/Banner';
import { Timestamp } from 'firebase/firestore';
import {
  chain, concat, every, filter, find, get, includes, isEmpty, last, map, omit, reject, size, some,
} from 'lodash';
import { mapState } from 'pinia';
import ProgramTargetsDB from 'src/fb/programTargets-db';
import SessionsDB from 'src/fb/sessions-db';
import {
  HrList,
  HrListItem,
  HrListSectionHeader,
  HrMenu,
  HrP,
  HrSegmentedBtn,
  HrSupportingBtn,
  HrTooltip,
} from 'src/design-system';
import DataCollectionProgram from 'src/mixins/DataCollectionProgram';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';
import { sessions } from 'src/store/sessions';
import {
  buildEoCandidates, buildEoStepGroups, hasScoreableContent, isEoStepSelected, resolveEoTask,
  splitEoLabels,
} from './sbtEoTask';

export default {

  name: 'SbtProgram',

  components: {
    HrP,
    TargetData,
    Banner,
    HrList,
    HrListItem,
    HrListSectionHeader,
    HrMenu,
    HrSegmentedBtn,
    HrSupportingBtn,
    HrTooltip,
  },

  mixins: [DataCollectionProgram, Interactions, Targets],

  emits: ['record-program-data', 'record', 'refresh-targets', 'record-multiple-data'],

  data() {
    return {
      proposedTargetChanges: [],

      nextTasks: [],

      targetResponse: null,

      eoStepManuallySelected: false,

      manuallySelectedTargetId: null,

      manuallySelectedCandidateId: null,

      manuallySelectedOption: null,

    };
  },

  computed: {
    ...mapState(sessions, ['activeSession']),

    isPinned() {
      return get(this.sessionProgram, 'pinned', false);
    },

    challengeModes() {
      const maxChars = this.isPinned ? 1 : 20;
      return [
        {
          value: 'easy',
          coopRatio: 0.4,
          targetRatio: 0.6667,
          label: this.$t('run.collectData.sbtModes.easy').slice(0, maxChars),
        },
        {
          value: 'normal',
          coopRatio: 0.6,
          targetRatio: 0.6667,
          label: this.$t('run.collectData.sbtModes.normal').slice(0, maxChars),
        },
        {
          value: 'challenging',
          coopRatio: 0.8,
          targetRatio: 0.6667,
          label: this.$t('run.collectData.sbtModes.challenging').slice(0, maxChars),
        },
      ];
    },

    hasAutoProgression() {
      return get(this.sessionProgram, ['program', 'sbtAutoProgression'], false);
    },

    sbtTrialsToSucceed() {
      return get(this.sessionProgram, ['program', 'sbtTrialsToSucceed'], 3);
    },

    programPromptLevelTemplate() {
      return find(this.activePromptLevels, { id: this.sessionProgram.program.promptLevelTemplate });
    }, // programPromptLevelTemplate()

    programPrompts() {
      return get(this.programPromptLevelTemplate, 'promptLevels');
    }, // programPrompts()

    defaultPromptLevel() {
      return find(this.programPrompts, { id: get(this.sessionProgram, ['program', 'defaultPromptLevel'], '') });
    }, // defaultPromptLevel()

    currentTrial() {
      return get(this.data, 'currentTrial', 1);
    }, // currentTrial()

    srTargets() {
      return filter(this.sortedTargets, { id: get(this.sessionProgram, ['program', 'srTarget']) });
    }, // srTargets()

    eoTargets() {
      return reject(this.sortedTargets, { id: get(this.sessionProgram, ['program', 'srTarget']) });
    }, // eoTargets()

    cabBranches() {
      return filter(this.sortedTargets, { dataType: 'cabBranch' });
    }, // cabBranches()

    currentTasks() {
      if (this.currentMode === 'SR') {
        return this.srTargets;
      }
      return this.eoTasks;
    },

    eoTasks() {
      if (this.currentEOTarget === null) {
        return [];
      }

      if (this.currentCABBranch === null) {
        // Not in a branch
        const currentTargetOrder = chain(this.sortedTargets)
          .find({ id: this.currentEOTarget })
          .get('order', 0)
          .value();
        return chain(this.eoTargets)
          .reject({ dataType: 'cabBranch' })
          .filter((target) => target.order <= currentTargetOrder)
          .defaultTo([])
          .value();
      }

      const eoSubTarget = (this.currentCABBranchTarget?.subTargets || [])
        .find((target) => target?.id === this.currentEOTarget);

      // Only surface the branch when its active sub-target has something to
      // score. Otherwise the branch renders as a dead, unclickable card.
      const showBranch = eoSubTarget && hasScoreableContent(eoSubTarget);

      return chain(this.eoTargets)
        .reject({ dataType: 'cabBranch' })
        .concat(showBranch ? [{
          ...this.currentCABBranchTarget,
          subTargets: [eoSubTarget],
        }] : [])
        .defaultTo([])
        .value();
    },

    challengeMode: {
      get() {
        return get(this.data, 'challengeMode', 'normal');
      },
      set(challengeMode) {
        this.$emit('record-program-data', this.sessionProgram, { challengeMode });
      },
    },

    currentMode() {
      return get(this.data, 'currentMode', 'SR');
    },

    nextMode() {
      return this.currentMode === 'SR' ? 'EO' : 'SR';
    },

    currentCABBranch() {
      return get(this.data, 'currentCABBranch', null);
    },

    currentCABBranchTarget() {
      return find(this.eoTargets, { id: this.currentCABBranch });
    },

    currentEOTarget() {
      return get(this.data, 'currentEOTarget', null);
    },

    nextLabel() {
      if (this.nextMode === 'EO') {
        return this.$t('run.collectData.textNextMode', { nextMode: this.nextMode });
      }
      return this.$t('run.collectData.textSwitchMode', { nextMode: this.nextMode });
    }, // nextLabel()

    nextTaskLabel() {
      return this.getTargetLabel(last(this.eoTasks));
    }, // nextTaskLabel()

    nextSubTaskLabel() {
      const targetTask = last(this.eoTasks);

      if (get(targetTask, 'dataType') === 'cabBranch') {
        return this.getTargetLabel(
          chain(targetTask)
            .get('subTargets')
            .last()
            .value(),
        );
      }

      return null;
    }, // nextSubTaskLabel()

    eoLabels() {
      return splitEoLabels({ taskLabel: this.nextTaskLabel, subTaskLabel: this.nextSubTaskLabel });
    }, // eoLabels()

    eoBranchLabel() {
      return this.eoLabels.branchLabel;
    }, // eoBranchLabel()

    eoStepLabel() {
      return this.eoLabels.stepLabel;
    }, // eoStepLabel()

    eoChipLabel() {
      return this.eoStepManuallySelected
        ? this.$t('run.collectData.textStepManuallySelected')
        : this.$t('run.collectData.textStepRandomized');
    }, // eoChipLabel()

    readyToProgress() {
      return (!isEmpty(this.proposedTargetChanges)) && (this.currentMode === 'SR');
    },

    // Grouped options for "Select step" menu (top-level steps & open CAB branches).
    eoStepGroups() {
      return buildEoStepGroups({
        sortedTargets: this.sortedTargets,
        srTargetId: get(this.sessionProgram, ['program', 'srTarget'], null),
        cabBranches: this.cabBranches,
        getLabel: (target) => get(target, 'label', null),
      });
    }, // eoStepGroups()

    // Validates that the manually picked EO step still exists in sortedTargets.
    // Prevents rendering empty cards if the selected target was mastered during onProceed.
    manualSelectionIsLive() {
      if (this.currentEOTarget === null) {
        return false;
      }
      const candidates = buildEoCandidates({
        sortedTargets: this.sortedTargets,
        srTargetId: get(this.sessionProgram, ['program', 'srTarget'], null),
        cabBranchId: this.currentCABBranch,
      });
      return candidates.some((target) => target.id === this.currentEOTarget);
    }, // manualSelectionIsLive()

  },
  watch: {
    sortedTargets: {
      immediate: false,
      deep: true,
      handler() {
        if (this.disable || (this.currentMode !== 'SR')) {
          return;
        }
        if (this.eoStepManuallySelected
          && this.manualSelectionIsLive
          && this.isEoOptionOffered(this.manuallySelectedOption)) {
          // Re-applies the manual choice to update targetResponse against refreshed targets.
          // Prevents targetResponse from remaining null after onProceed.
          this.onSelectEOStep(this.manuallySelectedOption);
          return;
        }
        const programData = this.getNewTargetEOTask();
        this.$emit('record-program-data', this.sessionProgram, programData);
      },
    },
  },

  created() {
    if (get(this.data, 'eoStepManuallySelected', false)) {
      // Target details are already in the session doc (currentEOTarget, currentCABBranch, rrTarget_*).
      // Restore the manual selection state which isn't preserved on remount.
      const rotatedChild = get(this.data, `rrTarget_${this.currentEOTarget}`, null);
      this.eoStepManuallySelected = true;
      this.manuallySelectedCandidateId = this.currentEOTarget;
      this.manuallySelectedTargetId = rotatedChild ?? this.currentEOTarget;
      this.manuallySelectedOption = {
        targetId: this.currentEOTarget,
        branchId: this.currentCABBranch,
        subTargetId: rotatedChild,
      };
    }

    if (this.currentEOTarget === null) {
      const programData = {
        ...this.getNewTargetEOTask(),
      };
      this.$emit('record-program-data', this.sessionProgram, programData);
    }
  },

  methods: {

    finishSession() {
      let finalData = chain(this.$refs)
        .get('targets')
        .map((targetRef) => {
          if (chain(targetRef).get('finishSession').isFunction().value()) {
            return targetRef.finishSession();
          }
          return null;
        })
        .flatten()
        .compact()
        .map((trialData) => {
          const finalRow = {
            ...trialData,
            trial: this.currentTrial,
            targetResponse: this.targetResponse,
          };
          if ((this.currentMode === 'EO') && this.isManuallySelectedTargetId(trialData.target)) {
            finalRow.isManuallySelected = true;
          }
          return finalRow;
        })
        .value();

      let skippedTrials = [];

      if (!chain(this.data)
        .get('data')
        .filter({ trial: this.currentTrial })
        .isEmpty()
        .value()) {
        const finalDataTargets = map(finalData, 'target');
        skippedTrials = reject(this.skipTrials(), (trialData) => includes(finalDataTargets, trialData.target));
        finalData = concat(finalData, skippedTrials);
      }
      return {
        finalData,
      };
    }, // finishSession()

    undoData() {
      if (this.disable) {
        return;
      }
      const lastTrial = chain(this.data)
        .get('data')
        .last()
        .value();
      const lastMode = chain(this.srTargets).map('id').includes(get(lastTrial, 'target')).value() ? 'SR' : 'EO';

      if (lastMode === this.currentMode) {
        return true;
      }
      if (lastMode === 'EO') {
        this.notifyNegative(this.$t('run.collectData.notifySbtUndoWarning'));
        return false;
      }
      const programData = {
        currentTrial: get(lastTrial, 'trial', 1),
        currentMode: 'SR',
      };

      this.$emit('record-program-data', this.sessionProgram, programData);
      return true;
    },

    onSwitchMode() {
      if ((this.currentMode === 'SR') || this.disable) {
        return;
      }
      this.switchMode();
    }, // onSwitchMode()

    onRecordData(sessionProgram, target, data) {
      const saveData = {
        ...data,
        trial: this.currentTrial,
        mode: this.challengeMode,
        targetResponse: this.targetResponse,
      };

      if ((this.currentMode === 'EO') && this.isManuallySelectedRow(target)) {
        saveData.isManuallySelected = true;
      }

      this.$emit('record', sessionProgram, target, saveData);

      if (this.currentMode === 'SR') {
        this.switchMode();
      }
    }, // onRecordData()

    // Matches the manually picked step directly or via parentTarget for sub-target components.
    isManuallySelectedRow(target) {
      const isMatchingTarget = this.manuallySelectedTargetId !== null
        && target?.id === this.manuallySelectedTargetId;

      const isMatchingCandidate = this.manuallySelectedCandidateId !== null
        && target?.parentTarget === this.manuallySelectedCandidateId;

      return isMatchingTarget || isMatchingCandidate;
    }, // isManuallySelectedRow()

    // finishSession rows name their target by id alone, so resolve it before asking.
    isManuallySelectedTargetId(targetId) {
      return this.isManuallySelectedRow(
        find(get(this.sessionProgram, ['program', 'targets'], []), { id: targetId }),
      );
    }, // isManuallySelectedTargetId()

    onRecordProgramData(sessionProgram, dataObj) {
      this.$emit('record-program-data', sessionProgram, dataObj);
    },

    onProceedDialog() {
      this.confirmDialog(
        null,
        this.$t('run.collectData.meetingCriteria'),
        () => {
          this.onProceed();
        },
        {
          ok: {
            label: this.$t('run.collectData.proceedWithCriteria'),
            flat: true,
          },
        },
      );
    },

    async onProceed() {
      const sessionId = get(this.activeSession, 'id');
      if (!sessionId) {
        return;
      }
      this.$q.loading.show();
      const proposedTargetChangeTargets = map(this.proposedTargetChanges, 'target');
      const targetStatusChanges = chain(this.activeSession)
        .get('targetStatusChanges', [])
        .reject((targetChange) => includes(proposedTargetChangeTargets, targetChange.target))
        .concat(chain(this.proposedTargetChanges).map((targetChange) => omit(targetChange, 'targetLabel')).value())
        .value();

      const sessionsDb = new SessionsDB();
      sessionsDb.set({ targetStatusChanges }, sessionId);

      const targetUpdatesActions = map(this.proposedTargetChanges, (targetChange) => {
        const programTargetsDb = new ProgramTargetsDB(targetChange.program);
        const targetProperties = { status: targetChange.toStatus };
        if (targetChange.toStatus === 'mastered') {
          targetProperties.masteryDate = Timestamp.now();
        }
        return programTargetsDb.set(targetProperties, targetChange.target);
      });
      await Promise.all(targetUpdatesActions);

      await this.updateTargetsPerStatus(this.sessionProgram.program);

      const targetLabels = chain(this.proposedTargetChanges)
        .filter({ toStatus: 'acquisition' })
        .map('targetLabel')
        .join(', ')
        .value();
      const targetCount = chain(this.proposedTargetChanges)
        .filter({ toStatus: 'acquisition' })
        .size()
        .value();

      this.notifySuccess(this.$tc('run.collectData.proceedSuccess', targetCount, {
        targetLabels,
      }));

      this.proposedTargetChanges = [];
      this.targetResponse = null;

      this.$emit('refresh-targets', get(this.sessionProgram, ['program', 'id']));
      this.$q.loading.hide();
    }, // onProceed()

    onDismissMastering() {
      this.proposedTargetChanges = [];
    }, // onDismissMastering()

    switchMode() {
      let programData = {};
      if (this.currentMode === 'SR') {
        programData = { currentMode: 'EO' };
      } else {
        const skippedTrials = this.skipTrials();

        const previousTrial = chain(this.data).get('data').last().value();
        const currentTrial = get(previousTrial, 'trial', 0);
        if (isEmpty(skippedTrials)) {
          this.checkMasteringCriteria(currentTrial);
        }

        programData = {
          currentTrial: currentTrial + 1,
          currentMode: 'SR',
          ...this.getNewTargetEOTask(),
        };

        if (!isEmpty(skippedTrials)) {
          this.$emit('record-multiple-data', this.sessionProgram, skippedTrials);
        }
      }

      this.$emit('record-program-data', this.sessionProgram, programData);
    }, // switchMode()

    skipTrials() {
      if ((this.currentMode !== 'EO') || !this.defaultPromptLevel) {
        return;
      }
      return chain(this.eoTasks)
        .flatMap((target) => {
          // Flatten out cabBranches
          if (target.dataType === 'cabBranch') {
            return get(target, 'subTargets', []);
          }
          return target;
        })
        .flatMap((target) => {
          if (isEmpty(target.subTargets)) {
            return target;
          }
          if (['shaping'].includes(target.dataType)) {
            // Shaping should not record default data
            // for other than the current shaping-target
            return chain(target).get('subTargets', []).last().value();
          }
          if (['randomRotation'].includes(target.dataType)) {
            // Random rotation should not record default data
            // for other than the current rr-target
            return chain(target)
              .get('subTargets', [])
              .find({ id: get(this.data, `rrTarget_${target.id}`, null) })
              .defaultTo(null)
              .value();
          }

          return get(target, 'subTargets', []);
        })
        .compact()
        // Disable if we already have data for this target at this trial
        .filter((target) => (chain(this.data)
          .get(['data'])
          .filter({ target: target.id })
          .filter({ trial: this.currentTrial })
          .isEmpty()
          .value()))
        .map((target) => {
          const saveSkippedData = {
            target: target.id,
            promptLevel: this.defaultPromptLevel.id,
            scoreValue: this.defaultPromptLevel.score,
            trial: this.currentTrial,
            targetResponse: this.targetResponse,
          };
          if (this.isManuallySelectedRow(target)) {
            saveSkippedData.isManuallySelected = true;
          }
          return saveSkippedData;
        })
        .value();
    }, // skipTrials()

    checkMasteringCriteria(currentTrial) {
      if (this.disable || !this.hasAutoProgression) {
        return;
      }

      const targetResponse = chain(this.data)
        .get('data')
        .filter({ trial: currentTrial })
        .last()
        .get('targetResponse', null)
        .value();

      if (targetResponse === null) {
        return;
      }

      const successCriteria = 100;
      const previousTrialsOutcome = chain(this.data)
        .get('data')
        .filter({ targetResponse })
        .groupBy('trial')
        // Don't count skipped trials:
        .reject((trialData) => (some(trialData, { scoreValue: null })))
        .map((trialData, trial) => ({
          trial: parseInt(trial, 10),
          isMastered: chain(trialData).meanBy('scoreValue').gte(successCriteria).value(),
        }))
        .sortBy('trial')
        .slice(-this.sbtTrialsToSucceed)
        .map('isMastered')
        .value();

      if ((size(previousTrialsOutcome) >= this.sbtTrialsToSucceed) && (every(previousTrialsOutcome))) {
        let targetResponseTarget = chain(this.sessionProgram)
          .get(['program', 'targets'])
          .find({ id: targetResponse })
          .defaultTo(null)
          .value();
        // RR, TA, Shaping targets - find the last open target within our target response
        const lastSubTarget = chain(this.sessionProgram)
          .get(['program', 'targets'])
          .filter({ parentTarget: targetResponse })
          .reject({ status: 'waiting' })
          .last()
          .defaultTo(null)
          .value();

        if (lastSubTarget !== null) {
          targetResponseTarget = lastSubTarget;
        }

        if (targetResponseTarget === null || targetResponseTarget?.status === 'mastered') {
          return;
        }

        // Check what to open next.
        // Priority:
        // Open next target with same parent target

        // Master current target
        // If no targets then recurse up and open next on upper level
        // If next target is a CAB branch then open all CAB branches that are waiting
        // Recurse down and open first target on targets (if any)
        this.masterTarget(targetResponseTarget);
      }
    }, // checkMasteringCriteria()

    openTarget(target) {
      this.addTargetChange({
        program: this.sessionProgram.program.id,
        target: target.id,
        targetLabel: target.label,
        fromStatus: target.status,
        toStatus: 'acquisition',
      });

      if (target.dataType === 'cabBranch') {
        // Check if it's a CAB Branch and then open other CAB Branches at same level
        const nextTarget = chain(this.sessionProgram)
          .get(['program', 'targets'])
          .reject({ id: get(this.sessionProgram, ['program', 'srTarget']) })
          .filter({ parentTarget: get(target, 'parentTarget', null) })
          .filter({ status: 'waiting' })
          .reject((targetObj) => targetObj.order <= target.order)
          .sortBy('order')
          .head()
          .value();
        if (get(nextTarget, 'dataType') === 'cabBranch') {
          this.openTarget(nextTarget);
        }
      }

      const subTarget = chain(this.sessionProgram)
        .get(['program', 'targets'])
        .reject({ id: get(this.sessionProgram, ['program', 'srTarget']) })
        .filter({ parentTarget: get(target, 'id', null) })
        .filter({ status: 'waiting' })
        .sortBy('order')
        .head()
        .value();

      if (!isEmpty(subTarget)) {
        this.openTarget(subTarget);
      }
    }, // openTarget()

    masterTarget(target) {
      this.addTargetChange({
        program: this.sessionProgram.program.id,
        target: target.id,
        targetLabel: target.label,
        fromStatus: target.status,
        toStatus: 'mastered',
      });

      const allTargetsAtLevelMastered = chain(this.sessionProgram)
        .get(['program', 'targets'])
        .reject({ id: get(this.sessionProgram, ['program', 'srTarget']) })
        .reject({ id: target.id })
        .filter({ parentTarget: get(target, 'parentTarget', null) })
        .reject({ status: 'mastered' })
        .reject({ status: 'closed' })
        .isEmpty()
        .value();

      if (allTargetsAtLevelMastered) {
        const parentTarget = chain(this.sessionProgram)
          .get(['program', 'targets'])
          .find({ id: target.parentTarget })
          .value();
        if (!isEmpty(parentTarget)) {
          this.masterTarget(parentTarget);
        }
      } else {
        const openTargetSameLevel = chain(this.sessionProgram)
          .get(['program', 'targets'])
          .reject({ id: get(this.sessionProgram, ['program', 'srTarget']) })
          .filter({ parentTarget: get(target, 'parentTarget', null) })
          .filter({ status: 'waiting' })
          .sortBy('order')
          .head()
          .value();

        if (!isEmpty(openTargetSameLevel)) {
          this.openTarget(openTargetSameLevel);
        }
      }
    }, // masterTarget()

    addTargetChange(newTargetChange) {
      if (find(this.proposedTargetChanges, { target: newTargetChange.target })) {
        this.proposedTargetChanges = chain(this.proposedTargetChanges)
          .map((targetChange) => {
            if (targetChange.target === newTargetChange.target) {
              return {
                ...targetChange,
                toStatus: newTargetChange.toStatus,
              };
            }
            return targetChange;
          })
          .value();
      } else {
        this.proposedTargetChanges = concat(this.proposedTargetChanges, newTargetChange);
      }
    },

    getNewTargetEOTask() {
      const branchIndex = Math.floor(Math.random() * size(this.cabBranches));
      const cabBranchId = get(this.cabBranches, [branchIndex, 'id'], null);
      const srTargetId = get(this.sessionProgram, ['program', 'srTarget'], null);

      const candidates = buildEoCandidates({ sortedTargets: this.sortedTargets, srTargetId, cabBranchId });
      const index = this.getRandomTargetIndex(size(candidates));

      const { programData, targetResponse } = resolveEoTask({
        candidates,
        index,
        cabBranchId,
        programTargets: get(this.sessionProgram, ['program', 'targets'], []),
        rotate: !this.disable,
      });

      this.targetResponse = targetResponse;
      // Clear manual flag only if set, avoiding unused keys in auto sessions.
      if (this.eoStepManuallySelected) {
        programData.eoStepManuallySelected = false;
      }
      this.eoStepManuallySelected = false;
      this.manuallySelectedTargetId = null;
      this.manuallySelectedCandidateId = null;
      this.manuallySelectedOption = null;

      return programData;
    }, // getNewTargetEOTask()

    // Returns the ID of the first open CAB branch that still has active candidates,
    // or null if none remain. Used to contextualise top-level manual picks.
    firstCabBranchWithCandidates(srTargetId) {
      const branchWithCandidates = find(this.cabBranches, (branch) => {
        const candidates = buildEoCandidates({
          sortedTargets: this.sortedTargets, srTargetId, cabBranchId: branch.id,
        });
        return candidates.some((target) => target.parentTarget === branch.id);
      });
      return get(branchWithCandidates, 'id', null);
    }, // firstCabBranchWithCandidates()

    isSelectedStep(option) {
      return isEoStepSelected({
        option,
        currentEOTarget: this.currentEOTarget,
        rotatedSubTargetId: get(this.data, `rrTarget_${option.targetId}`, null),
      });
    }, // isSelectedStep()

    // Checks if the option is still offered in the rendered menu (eoStepGroups).
    isEoOptionOffered(option) {
      return this.eoStepGroups.some((group) => group.options.some((offered) => (
        (offered.targetId === option?.targetId)
        && (offered.subTargetId === option?.subTargetId)
        && (offered.branchId === option?.branchId)
      )));
    }, // isEoOptionOffered()

    onSelectEOStep(option) {
      if (this.disable || !option) {
        return;
      }

      const srTargetId = get(this.sessionProgram, ['program', 'srTarget'], null);
      const cabBranchId = option.branchId ?? this.firstCabBranchWithCandidates(srTargetId);
      const candidates = buildEoCandidates({ sortedTargets: this.sortedTargets, srTargetId, cabBranchId });
      const index = candidates.findIndex((target) => target.id === option.targetId);

      if ((index === -1) || !this.isEoOptionOffered(option)) {
        // The menu went stale between opening it and picking - the step, or the rotation child
        // under it, was mastered elsewhere mid-session. Leave the already-rolled task in place.
        return;
      }

      const { programData, targetResponse, scoredTargetId } = resolveEoTask({
        candidates,
        index,
        cabBranchId,
        programTargets: get(this.sessionProgram, ['program', 'targets'], []),
        rotate: !this.disable,
        forcedSubTargetId: option.subTargetId ?? null,
      });

      this.targetResponse = targetResponse;
      this.eoStepManuallySelected = true;
      this.manuallySelectedTargetId = scoredTargetId;
      this.manuallySelectedCandidateId = programData.currentEOTarget;
      this.manuallySelectedOption = option;

      programData.eoStepManuallySelected = true;

      this.$emit('record-program-data', this.sessionProgram, programData);
    }, // onSelectEOStep()

    // Get a random number that's biased towards the lower or higher end
    // If challengeLevel < 0 it will be biased towards the lower end
    // If challengeLevel == 0 it will be equally distributed
    // If challengeLevel > 0 it will be biased towards the higher end
    getRandomTargetIndex(numTargets) {
      const nonCoopTargetCount = 2;

      const coopRatio = chain(this.challengeModes)
        .find({ value: this.challengeMode })
        .get('coopRatio', 0)
        .value();
      const targetRatio = chain(this.challengeModes)
        .find({ value: this.challengeMode })
        .get('targetRatio', 0)
        .value();

      // We assume that the first two targets are non-Coop targets
      // so if we are not yet working with coop-targets we will
      // just return a random target
      if (numTargets <= nonCoopTargetCount) {
        return Math.floor(Math.random() * numTargets);
      }

      // If we have started coop the algorithm (in normal mode) is:
      // 60% chance we are picking a Coop target and 40% chance (of all)
      // that we are picking the current (max) target

      if (Math.random() >= coopRatio) {
        // We did not get a coop target - equal chance for Comms/TR targets
        return Math.floor(Math.random() * nonCoopTargetCount);
      }

      if (numTargets === (nonCoopTargetCount + 1)) {
        // We got a coop target and there is only one coop target available
        return numTargets - 1;
      }
      if (Math.random() < targetRatio) {
        // We got coop and current target - return last possible target
        return numTargets - 1;
      }

      // Select a random coop target that's not the current target
      return Math.floor(
        Math.random()
        * (numTargets - (nonCoopTargetCount + 1)),
      ) + nonCoopTargetCount;
    }, // getRandomTargetIndex()

    getTargetLabel(target) {
      if (get(target, 'dataType') === 'randomRotation') {
        return chain(target)
          .get('subTargets', [])
          .find({ id: get(this.data, `rrTarget_${target.id}`, null) })
          .get('label', null)
          .value();
      }
      return get(target, 'label', null);
    },

  },
};
</script>
<style lang="scss">
/* Cancels `min-width: max-content` to let QMenu `fit` match the button's exact width. */
.sbt-select-step-menu {
  width: 0;

  .q-item__label {
    white-space: normal;
    word-break: break-word;

    &:not(.q-item__label--caption):not(.q-item__label--header) {
      font-size: var(--app-type-body-large-size);
    }
    &--header {
      text-transform: uppercase;
      font-size: var(--app-type-label-medium-size);
      font-weight: var(--app-type-label-medium-weight);

      &:first-child {
        padding-top: var(--app-comp-page-padding-md, 16px);
      }
    }
  }

  .q-item.q-item--active {
    background-color: var(--app-primary-container);
    color: var(--app-on-primary-container);
  }
}
</style>

<style lang="scss" scoped>
.sbt-current-card {
  background-color: var(--app-primary-container);
}

.sbt-current-card--wide.col-auto,
.sbt-next-card--wide.col-auto {
  min-width: 258px;
}

.sbt-current-label,
.sbt-current-mode {
  color: var(--app-on-primary-container);
}

.sbt-current-label--eo,
.sbt-current-mode--eo,
.sbt-next-eo-label,
.sbt-next-eo-branch {
  color: var(--app-on-warning-container);
}

.sbt-current-mode {
  margin-top: calc(8px + var(--app-type-body-small-line-height));
}

.sbt-current-trial {
  border-radius: var(--app-shape-corner-medium, 12px);
  background-color: var(--app-on-primary-container);
  color: var(--app-primary-container);
}

.sbt-current-modes {
  border-radius: var(--app-shape-corner-large, 16px);
  border: none;
  :deep(.q-btn) {
    min-height: var(--app-comp-button-container-height-sm);
    font-size: var(--app-type-body-small-size);
  }
}

.sbt-current-card--eo,
.sbt-next-card--eo {
  background-color: var(--app-warning-container);
}

.sbt-next-eo-branch {
  min-height: var(--app-type-body-small-line-height);
}

.sbt-next-eo-name {
  color: var(--app-on-surface, #1B1D1F);
}

.sbt-next-eo-action {
  min-height: var(--app-comp-button-container-height-sm);
  background: var(--app-surface, #FFFFFF);
  box-shadow: var(--app-elevation-1);

  &:not(.q-btn--round) {
    border-radius: var(--app-shape-corner-full, 9999px);
  }

  :deep(.q-btn__content) {
    color: var(--app-on-surface, #1B1D1F);
    font-size: var(--app-type-body-small-size);
  }

  &.disabled {
    box-shadow: none;
  }
}

.sbt-next-eo-chip {
  border-radius: var(--app-shape-corner-full, 9999px);

  &--randomized {
    background-color: var(--app-success-container);
    color: var(--app-on-success-container);
  }

  &--manual {
    background-color: var(--app-tertiary-container);
    color: var(--app-on-tertiary-container);
  }
}

.sbt-next-eo-step {
  word-break: break-word;
}

:deep(.q-card),
:deep(.rounded-borders) {
  border-radius: var(--app-shape-corner-small, 8px);
}
</style>
